import * as moment from 'moment-timezone';

import type { EventWaitlistSummary } from '../scheduled-event';
import { dateStringFromISO8601, propExists, timeStringFromISO8601 } from '../../reporting-core/utils';
import { getScheduleTypeDisplay } from '../event-type';

export const COLUMN_NAME_EVENT_ID = 'Event ID';
export const COLUMN_NAME_EVENT_NAME = 'Event Name';
export const COLUMN_NAME_EVENT_OCCURRENCE_NAME = 'Event Occurrence Name';
export const COLUMN_NAME_EVENT_START_DATE = 'Event Start Date';
export const COLUMN_NAME_EVENT_START_TIME = 'Event Start Time';
export const COLUMN_NAME_EVENT_START = 'Event Start Timestamp';
export const COLUMN_NAME_EVENT_END = 'Event End Timestamp';
export const COLUMN_NAME_TIME_ZONE = 'Time Zone';
export const COLUMN_NAME_EVENT_NUM_PARTS = 'Event Number Of Parts';
export const COLUMN_NAME_EVENT_IS_PRIVATE = 'Event Is Private?';
export const COLUMN_NAME_EVENT_CAPACITY = 'Event Capacity';
export const COLUMN_NAME_EVENT_TYPE_ID = 'Event Type ID';
export const COLUMN_NAME_EVENT_TYPE_NAME = 'Event Type Name';
export const COLUMN_NAME_EVENT_SCHEDULE_TYPE = 'Event Schedule Type';
export const COLUMN_NAME_LOCATION_ID = 'Location ID';
export const COLUMN_NAME_LOCATION_NAME = 'Location Name';
export const COLUMN_NAME_WAITING_COUNT = '# of Waiting Entries';
export const COLUMN_NAME_PENDING_COUNT = '# of Pending Entries';
export const COLUMN_NAME_ACTIVE_COUNT = '# of Active Waitlist Entries';
export const COLUMN_NAME_STAFF_SITE_URL = 'Staff Site URL';

export const COLUMN_NAMES: string[] = [
  COLUMN_NAME_EVENT_ID,
  COLUMN_NAME_EVENT_NAME,
  COLUMN_NAME_EVENT_OCCURRENCE_NAME,
  COLUMN_NAME_EVENT_START_DATE,
  COLUMN_NAME_EVENT_START_TIME,
  COLUMN_NAME_EVENT_START,
  COLUMN_NAME_EVENT_END,
  COLUMN_NAME_TIME_ZONE,
  COLUMN_NAME_EVENT_NUM_PARTS,
  COLUMN_NAME_EVENT_IS_PRIVATE,
  COLUMN_NAME_EVENT_CAPACITY,
  COLUMN_NAME_EVENT_TYPE_ID,
  COLUMN_NAME_EVENT_TYPE_NAME,
  COLUMN_NAME_EVENT_SCHEDULE_TYPE,
  COLUMN_NAME_LOCATION_ID,
  COLUMN_NAME_LOCATION_NAME,
  COLUMN_NAME_WAITING_COUNT,
  COLUMN_NAME_PENDING_COUNT,
  COLUMN_NAME_ACTIVE_COUNT,
  COLUMN_NAME_STAFF_SITE_URL,
];

export const COLUMN_HELP_TEXT: {[key: string]: string} = {
  [COLUMN_NAME_EVENT_START]: "ISO 8601, in the event's location time zone.",
  [COLUMN_NAME_EVENT_END]: "The end of the event's last part. ISO 8601, in the event's location time zone.",
  [COLUMN_NAME_ACTIVE_COUNT]: 'The number of Waiting entries plus the number of Pending entries.',
};

function zonedTimestamp(iso8601datetime: string, timeZone: string): string {
  return moment.tz(iso8601datetime, timeZone).format();
}

/**
 * The Staff Site URL needs the browser origin and the Angular router path, so
 * the caller injects it, keeping this module loadable under plain Node.
 */
export function makeGetContentItem(
  getStaffSiteUrl: (summary: EventWaitlistSummary) => string
): (summary: EventWaitlistSummary, columnName: string) => string {
  return (summary: EventWaitlistSummary, selectedColumnName: string): string => {
    switch (selectedColumnName) {
      case COLUMN_NAME_EVENT_ID:
        return summary.id.toString();
      case COLUMN_NAME_EVENT_NAME:
        if (!summary.occurrence_name) { return summary.event_type_name; }
        return summary.event_type_name + ' - ' + summary.occurrence_name;
      case COLUMN_NAME_EVENT_OCCURRENCE_NAME:
        return summary.occurrence_name || null;
      case COLUMN_NAME_EVENT_START_DATE:
        return dateStringFromISO8601(summary.first_start_datetime, summary.time_zone);
      case COLUMN_NAME_EVENT_START_TIME:
        return timeStringFromISO8601(summary.first_start_datetime, summary.time_zone);
      case COLUMN_NAME_EVENT_START:
        return zonedTimestamp(summary.first_start_datetime, summary.time_zone);
      case COLUMN_NAME_EVENT_END:
        if (!propExists(summary.last_end_datetime)) { return null; }
        return zonedTimestamp(summary.last_end_datetime, summary.time_zone);
      case COLUMN_NAME_TIME_ZONE:
        return summary.time_zone;
      case COLUMN_NAME_EVENT_NUM_PARTS:
        return summary.event_part_count.toString();
      case COLUMN_NAME_EVENT_IS_PRIVATE:
        return summary.is_private ? 'True' : 'False';
      case COLUMN_NAME_EVENT_CAPACITY:
        if (!propExists(summary.capacity)) { return null; }
        return summary.capacity.toString();
      case COLUMN_NAME_EVENT_TYPE_ID:
        return summary.event_type_id.toString();
      case COLUMN_NAME_EVENT_TYPE_NAME:
        return summary.event_type_name;
      case COLUMN_NAME_EVENT_SCHEDULE_TYPE:
        return getScheduleTypeDisplay(summary.event_type_schedule_type);
      case COLUMN_NAME_LOCATION_ID:
        return summary.location_id.toString();
      case COLUMN_NAME_LOCATION_NAME:
        return summary.location_name;
      case COLUMN_NAME_WAITING_COUNT:
        return summary.waiting_count.toString();
      case COLUMN_NAME_PENDING_COUNT:
        return summary.pending_count.toString();
      case COLUMN_NAME_ACTIVE_COUNT:
        return (summary.waiting_count + summary.pending_count).toString();
      case COLUMN_NAME_STAFF_SITE_URL:
        return getStaffSiteUrl(summary);
      default:
        return 'COLUMN NOT IMPLEMENTED';
    }
  };
}
